import { Eye, EyeOff, Search, X } from "lucide-react";
import type { Ref } from "react";

import {
    InputGroup,
    InputGroupAddon,
    InputGroupButton,
    InputGroupInput,
} from "@/components/ui/input-group";
import { Kbd } from "@/components/ui/kbd";
import { cn } from "@/lib/utils";

interface FieldsToolbarProps {
    /** Forwarded to the search input so the slash-shortcut can focus it. */
    searchInputRef?: Ref<HTMLInputElement>;
    searchQuery: string;
    onSearchChange: (next: string) => void;
    /** Hidden-empty count drives the toggle; rendered only when > 0. */
    hiddenCount: number;
    showEmpty: boolean;
    onToggleEmpty: () => void;
    /** Slot for the AdminExtractionMenu trigger; absent for non-admins. */
    adminMenu?: React.ReactNode;
    /** Hide the search row in states with nothing to search (loading/empty). */
    showSearch?: boolean;
    /**
     * Gate for the empty-fields toggle pill. Counts stay unreliable until
     * extraction reports `complete`, so we suppress the toggle during
     * pending/processing/failed/not_applicable, even when a partial result
     * set (e.g. a COI-only run) makes `hiddenCount > 0`.
     */
    extractionComplete: boolean;
}

// Sticky two-row toolbar for the document detail panel's fields section.
// Top row: FIELDS label + showEmpty toggle + admin menu. Bottom row: search.
//
// The `-mx-5 px-5` bleed is load-bearing: this toolbar renders inside the
// panel's scroll container which has `px-5`. Without the bleed, the
// `border-b` divider would stop short of the panel edges. The negative
// margin extends the toolbar's background + border to the panel's full
// width while the inner `px-5` keeps content aligned with sibling rows.
// If the parent's horizontal padding changes, update both values in lockstep.
export function FieldsToolbar({
    searchInputRef,
    searchQuery,
    onSearchChange,
    hiddenCount,
    showEmpty,
    onToggleEmpty,
    adminMenu,
    showSearch = true,
    extractionComplete,
}: FieldsToolbarProps) {
    const showToggle = extractionComplete && (hiddenCount > 0 || showEmpty);
    const hasQuery = searchQuery.length > 0;

    // Refocus the input after clearing so the user can keep typing. The
    // InputGroupAddon's own click-handler refocuses on non-button clicks,
    // but bails inside this button — so we do it explicitly. The slash
    // shortcut uses the same ref shape (object ref with `.current`).
    const handleClear = () => {
        onSearchChange("");
        if (
            searchInputRef &&
            typeof searchInputRef === "object" &&
            "current" in searchInputRef
        ) {
            searchInputRef.current?.focus();
        }
    };

    return (
        <div
            role="toolbar"
            aria-label="Fields"
            className="bg-background border-border/60 before:bg-background sticky top-0 z-20 -mx-5 flex flex-col gap-2 border-b px-5 py-2 [--fields-toolbar-h:5rem] before:absolute before:inset-x-0 before:bottom-full before:h-2"
        >
            <div className="flex h-7 items-center gap-2">
                <span className="text-muted-foreground text-[11px] font-semibold tracking-wider uppercase">
                    Fields
                </span>
                <div className="ml-auto flex items-center gap-1">
                    {showToggle && (
                        <button
                            type="button"
                            onClick={onToggleEmpty}
                            aria-pressed={showEmpty}
                            aria-label={
                                showEmpty
                                    ? "Hide empty fields"
                                    : `Show ${hiddenCount} empty field${hiddenCount === 1 ? "" : "s"}`
                            }
                            className={cn(
                                "inline-flex h-7 items-center gap-1.5 rounded-full px-2.5 text-[11px] font-medium transition-colors duration-150 outline-none",
                                "focus-visible:ring-ring/40 focus-visible:ring-2",
                                showEmpty
                                    ? "bg-muted text-foreground hover:bg-muted/70"
                                    : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                            )}
                        >
                            {showEmpty ? (
                                <>
                                    <Eye className="size-3" strokeWidth={2.2} />
                                    <span>Hide empty</span>
                                </>
                            ) : (
                                <>
                                    <EyeOff
                                        className="size-3 opacity-70"
                                        strokeWidth={2.2}
                                    />
                                    <span>
                                        <span className="text-foreground font-semibold tabular-nums">
                                            {hiddenCount}
                                        </span>{" "}
                                        empty
                                    </span>
                                </>
                            )}
                        </button>
                    )}
                    {adminMenu}
                </div>
            </div>
            {showSearch && (
                <InputGroup>
                    <InputGroupAddon>
                        <Search
                            aria-hidden
                            className="text-muted-foreground/70 size-3.5"
                            strokeWidth={2.2}
                        />
                    </InputGroupAddon>
                    <InputGroupInput
                        ref={searchInputRef}
                        type="search"
                        value={searchQuery}
                        onChange={(e) => onSearchChange(e.target.value)}
                        aria-label="Search fields"
                        placeholder="Search fields"
                        // Suppress Safari/Chrome's native search-cancel button so
                        // our InputGroupButton owns the X affordance and we
                        // don't get two clear controls fighting for the slot.
                        className="text-sm [&::-webkit-search-cancel-button]:appearance-none"
                    />
                    <InputGroupAddon align="inline-end">
                        {hasQuery ? (
                            <InputGroupButton
                                size="icon-xs"
                                aria-label="Clear search"
                                onClick={handleClear}
                            >
                                <X strokeWidth={2.2} />
                            </InputGroupButton>
                        ) : (
                            <Kbd className="hidden sm:inline-flex">/</Kbd>
                        )}
                    </InputGroupAddon>
                </InputGroup>
            )}
        </div>
    );
}
